/**
 * @fileoverview Admin Vendors Page Component
 *
 * Displays all vendors with search, filtering, and card/list view toggle.
 * Primary admin interface for vendor management.
 *
 * Features:
 * - Search by vendor name, contact name, phone, or email
 * - Default view shows Active vendors only; toggle to include Inactive
 * - Filter by vendor type and flags (showroom / client-facing)
 * - Toggle between card and list views
 * - Click to open vendor details modal
 * - Mobile-optimized for field use
 *
 * @requires react
 * @requires react-icons/fa
 * @version 1.0.0
 */

import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  FaSearch,
  FaFilter,
  FaTh,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaTools,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaGlobe,
  FaSpinner,
  FaExclamationTriangle,
  FaUserTie,
  FaExternalLinkAlt,
  FaHardHat,
  FaStore,
  FaStoreSlash,
  FaUsers,
  FaUsersSlash,
  FaStickyNote,
} from "react-icons/fa";
import {
  useAdminVendors,
  useVendorCategories,
  useCreateVendor,
  useUpdateVendor,
} from "../../hooks/useAdminVendors";
import useUserPreferences from "../../hooks/useUserPreferences";
import VendorModal from "../../components/admin/VendorModal";
import VendorInfoModal from "../../components/admin/VendorInfoModal";
import AdminPageBanner from "../../components/admin/AdminPageBanner";
import { formatPhoneDisplay } from "../../utils/phoneUtils";
import "./AdminVendors.css";

/* ============================================
   CONSTANTS
   ============================================ */

/**
 * Showroom filter options
 * @constant {Array<Object>}
 */
const SHOWROOM_OPTIONS = [
  { value: "all", label: "All Vendors" },
  { value: "true", label: "Has Showroom" },
  { value: "false", label: "No Showroom" },
];

/**
 * Client-facing filter options
 * @constant {Array<Object>}
 */
const CLIENT_FACING_OPTIONS = [
  { value: "all", label: "All Vendors" },
  { value: "true", label: "Client-Facing" },
  { value: "false", label: "Not Client-Facing" },
];

/**
 * Returns vendor status color class
 * @param {string} status - Vendor status
 * @returns {string} CSS class name
 */
const getVendorStatusColor = (status) => {
  switch (status) {
    case "Active":
      return "admin-vendors-status-active";
    case "Inactive":
      return "admin-vendors-status-inactive";
    default:
      return "admin-vendors-status-default";
  }
};

/**
 * Returns vendor category color class.
 * Per UI requirements, all category pills use the same default scheme so
 * we don't need to maintain a category->color mapping.
 * @returns {string} CSS class name
 */
const getVendorCategoryColor = () => "type-default";

/**
 * Normalizes Airtable boolean values that may come back as true/false, "true"/"false", or 1/0.
 * @param {any} value - Raw value
 * @returns {boolean} Whether the value is truthy boolean
 */
const toBoolean = (value) => value === true || value === "true" || value === 1;

/**
 * Builds the tag pills to display for a vendor.
 * Includes ALL vendor categories as pills, and conditionally includes showroom/client-facing.
 *
 * Note: Airtable may return a single string or an array for multi-select fields.
 * @param {Object} vendor - Vendor record
 * @returns {Array<{ key: string, label: string, className: string }>} Tag config
 */
const buildVendorTags = (vendor) => {
  const rawCategories = Array.isArray(vendor.vendorCategory)
    ? vendor.vendorCategory
    : vendor.vendorCategory
      ? [vendor.vendorCategory]
      : [];

  const categories = Array.from(
    new Set(rawCategories.map((c) => `${c}`.trim()).filter(Boolean)),
  );

  const tags = categories.length
    ? categories.map((category, idx) => ({
        key: `vendor-type-${idx}-${category}`,
        label: category,
        className: getVendorCategoryColor(category),
      }))
    : [
        {
          key: "vendor-type-0-General",
          label: "Other",
          className: getVendorCategoryColor(""),
        },
      ];

  if (toBoolean(vendor.hasShowroom)) {
    tags.push({ key: "showroom", label: "Showroom", className: "tag-flag" });
  }
  if (toBoolean(vendor.isClientFacing)) {
    tags.push({
      key: "client-facing",
      label: "Client-Facing",
      className: "tag-flag",
    });
  }

  return tags;
};

/* ============================================
   SUB-COMPONENTS
   ============================================ */

/**
 * Search and control bar component
 * @param {Object} props - Component props
 */
const SearchFilterBar = ({
  searchTerm,
  onSearchChange,
  viewMode,
  onViewModeChange,
  showFilters,
  onToggleFilters,
  activeFilterCount,
  showInactive,
  onToggleShowInactive,
  onAddClick,
}) => (
  <div className="admin-vendors-controls">
    {/* Search Input */}
    <div className="admin-vendors-search">
      <FaSearch className="admin-vendors-search-icon" />
      <input
        type="text"
        placeholder="Search vendors, emails, or phone numbers..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="admin-vendors-search-input"
      />
    </div>

    {/* Control Buttons */}
    <div className="admin-vendors-control-buttons">
      {/* Show Inactive Toggle (Switch) */}
      <div className="admin-vendors-inactive-toggle">
        <label className="admin-vendors-toggle-label">
          <span className="admin-vendors-toggle-text">Show Inactive</span>
          <div className="admin-vendors-toggle-switch">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => onToggleShowInactive(e.target.checked)}
              aria-label="Show inactive vendors"
            />
            <span className="admin-vendors-toggle-slider"></span>
          </div>
        </label>
      </div>

      {/* Filter Toggle */}
      <button
        className={`admin-vendors-filter-btn ${showFilters ? "active" : ""}`}
        onClick={onToggleFilters}
      >
        <FaFilter />
        <span>Filters</span>
        {activeFilterCount > 0 && (
          <span className="admin-vendors-filter-count">
            {activeFilterCount}
          </span>
        )}
        {showFilters ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {/* View Mode Toggle */}
      <div className="admin-vendors-view-toggle">
        <button
          className={`admin-vendors-view-btn ${
            viewMode === "card" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("card")}
        >
          <FaTh /> <span className="hide-sm">Card View</span>
        </button>
        <button
          className={`admin-vendors-view-btn ${
            viewMode === "list" ? "active" : ""
          }`}
          onClick={() => onViewModeChange("list")}
        >
          <FaList /> <span className="hide-sm">List View</span>
        </button>
      </div>
    </div>
  </div>
);

/**
 * Filter panel component
 * @param {Object} props - Component props
 */
const FilterPanel = ({
  filters,
  onFilterChange,
  onClearFilters,
  isVisible,
  categoryOptions,
}) => {
  if (!isVisible) return null;

  return (
    <div className="admin-vendors-filters">
      <div className="admin-vendors-filter-row">
        {/* Vendor Category Filter */}
        <div className="admin-vendors-filter-group">
          <label className="admin-vendors-filter-label">Vendor Category</label>
          <select
            value={filters.vendorCategory}
            onChange={(e) => onFilterChange("vendorCategory", e.target.value)}
            className="admin-vendors-filter-select"
          >
            {categoryOptions.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        {/* Has Showroom Filter */}
        <div className="admin-vendors-filter-group">
          <label className="admin-vendors-filter-label">
            <FaStore /> Showroom
          </label>
          <select
            value={filters.hasShowroom}
            onChange={(e) => onFilterChange("hasShowroom", e.target.value)}
            className="admin-vendors-filter-select"
          >
            {SHOWROOM_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Is Client-Facing Filter */}
        <div className="admin-vendors-filter-group">
          <label className="admin-vendors-filter-label">
            <FaUsers /> Client-Facing
          </label>
          <select
            value={filters.isClientFacing}
            onChange={(e) => onFilterChange("isClientFacing", e.target.value)}
            className="admin-vendors-filter-select"
          >
            {CLIENT_FACING_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters */}
        <button
          className="admin-vendors-clear-filters"
          onClick={onClearFilters}
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

/**
 * Vendor card for grid view
 * @param {Object} props - Component props
 */
const VendorCard = ({ vendor, onClick }) => {
  const categories = Array.isArray(vendor.vendorCategory)
    ? vendor.vendorCategory.filter(Boolean)
    : vendor.vendorCategory
      ? [vendor.vendorCategory]
      : [];

  const notesText = (vendor.notes || "").trim();

  const hasShowroom = toBoolean(vendor.hasShowroom);
  const isClientFacing = toBoolean(vendor.isClientFacing);

  // Get logo thumbnail URL (first attachment if available)
  const logoUrl =
    vendor.logo && vendor.logo.length > 0
      ? vendor.logo[0].thumbnails?.medium?.url || vendor.logo[0].url
      : null;

  return (
    <div className="admin-vendors-card" onClick={() => onClick(vendor.id)}>
      {/* Card Header */}
      <div
        className={`admin-vendors-card-header ${getVendorStatusColor(vendor.status)}`}
      >
        {/* LEFT: Category pills (all default scheme) */}
        <div className="admin-vendors-card-header-left">
          {categories.length ? (
            categories.map((category, idx) => (
              <span
                key={`cat-${idx}-${category}`}
                className={`admin-vendors-tag-pill ${getVendorCategoryColor()}`}
              >
                {category}
              </span>
            ))
          ) : (
            <span
              className={`admin-vendors-tag-pill ${getVendorCategoryColor()}`}
            >
              Other
            </span>
          )}
        </div>

        {/* RIGHT: Icon pills (show true/false as icons) */}
        <div className="admin-vendors-card-header-right">
          <span
            className={`admin-vendors-icon-pill ${
              hasShowroom ? "is-true" : "is-false"
            }`}
            data-tooltip-bottom={hasShowroom ? "Has Showroom" : "No Showroom"}
            aria-label={hasShowroom ? "Has Showroom" : "No Showroom"}
          >
            {hasShowroom ? <FaStore /> : <FaStoreSlash />}
          </span>

          <span
            className={`admin-vendors-icon-pill ${
              isClientFacing ? "is-true" : "is-false"
            }`}
            data-tooltip-bottom={
              isClientFacing ? "Client-Facing" : "Not Client-Facing"
            }
            aria-label={isClientFacing ? "Client-Facing" : "Not Client-Facing"}
          >
            {isClientFacing ? <FaUsers /> : <FaUsersSlash />}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="admin-vendors-card-body">
        {/* Vendor Name with Logo */}
        <div className="admin-vendors-card-title-row">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${vendor.vendorName} logo`}
              className="admin-vendors-card-logo"
            />
          ) : (
            <span className="admin-vendors-no-logo-icon">
              <FaHardHat />
            </span>
          )}

          <h3 className="admin-vendors-card-title">{vendor.vendorName}</h3>
        </div>

        <div className="admin-vendors-card-info">
          {/* Contact Person */}
          <div className="admin-vendors-card-row">
            <FaUserTie className="admin-vendors-card-icon" />
            <span>{vendor.contactName || "No assigned contact person"}</span>
          </div>

          {/* Phone */}
          <div className="admin-vendors-card-row">
            <FaPhone className="admin-vendors-card-icon" />
            <span>{formatPhoneDisplay(vendor.contactPhone) || "No phone"}</span>
          </div>

          {/* Email */}
          <div className="admin-vendors-card-row">
            <FaEnvelope className="admin-vendors-card-icon" />
            <span>{vendor.contactEmail || "No email"}</span>
          </div>

          {/* Address */}
          <div className="admin-vendors-card-row">
            <FaMapMarkerAlt className="admin-vendors-card-icon" />
            <span className="admin-vendors-card-address">
              {vendor.address || "No address"}
            </span>
          </div>

          {/* Website */}
          <div className="admin-vendors-card-row">
            <FaGlobe className="admin-vendors-card-icon" />
            <span>{vendor.website || "No website"}</span>
          </div>
        </div>
      </div>

      {/* Card Footer - Notes */}
      <div className="admin-vendors-card-footer">
        <div
          className={`admin-vendors-card-notes ${notesText ? "" : "is-empty"}`}
        >
          <label>
            <FaStickyNote />
          </label>
          <span>{notesText || "No vendor notes"}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Vendor row for list view
 * @param {Object} props - Component props
 */
const VendorRow = ({ vendor, onClick }) => {
  // Handle vendorCategory array (Multiple Select field)
  const categoryDisplay = Array.isArray(vendor.vendorCategory)
    ? vendor.vendorCategory.join(", ")
    : vendor.vendorCategory || "—";

  // Get first vendor category for color coding
  const categoryForColor = Array.isArray(vendor.vendorCategory)
    ? vendor.vendorCategory[0]
    : vendor.vendorCategory;

  // Get logo thumbnail URL (first attachment if available)
  const logoUrl =
    vendor.logo && vendor.logo.length > 0
      ? vendor.logo[0].thumbnails?.small?.url || vendor.logo[0].url
      : null;

  const hasShowroom = toBoolean(vendor.hasShowroom);
  const isClientFacing = toBoolean(vendor.isClientFacing);

  return (
    <tr className="admin-vendors-row" onClick={() => onClick(vendor.id)}>
      <td className="admin-vendors-cell-name">
        <div className="admin-vendors-name-cell">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${vendor.vendorName} logo`}
              className="admin-vendors-row-logo"
            />
          ) : (
            <span
              className={`admin-vendors-type-icon ${getVendorCategoryColor(
                categoryForColor,
              )}`}
            >
              <FaTools />
            </span>
          )}
          <div>
            <span className="admin-vendors-row-title">{vendor.vendorName}</span>
            <span className="admin-vendors-row-subtitle">
              {vendor.contactName || "No assigned contact person"}
            </span>
          </div>
        </div>
      </td>
      <td className="admin-vendors-cell hide-md">
        {formatPhoneDisplay(vendor.contactPhone) || "—"}
      </td>
      <td className="admin-vendors-cell">{categoryDisplay}</td>
      <td className="admin-vendors-cell admin-vendors-cell-center">
        <span
          className={`admin-vendors-icon-pill ${
            isClientFacing ? "is-true" : "is-false"
          }`}
          aria-label={isClientFacing ? "Client-Facing" : "Not Client-Facing"}
        >
          {isClientFacing ? <FaUsers /> : <FaUsersSlash />}
        </span>
      </td>
      <td className="admin-vendors-cell admin-vendors-cell-center">
        <span
          className={`admin-vendors-icon-pill ${
            hasShowroom ? "is-true" : "is-false"
          }`}
          aria-label={hasShowroom ? "Has Showroom" : "No Showroom"}
        >
          {hasShowroom ? <FaStore /> : <FaStoreSlash />}
        </span>
      </td>
      <td className="admin-vendors-cell">
        <span
          className={`admin-vendors-status-badge ${
            vendor.status === "Active" ? "badge-active" : "badge-inactive"
          }`}
        >
          {vendor.status}
        </span>
      </td>
    </tr>
  );
};

/**
 * Mobile accordion item for list view
 * @param {Object} props - Component props
 */
const MobileAccordionItem = ({ vendor, isExpanded, onToggle, onView }) => {
  // Handle vendorCategory array (Multiple Select field)
  const categoryDisplay = Array.isArray(vendor.vendorCategory)
    ? vendor.vendorCategory.join(", ")
    : vendor.vendorCategory || "Other";

  // Get logo thumbnail URL (first attachment if available)
  const logoUrl =
    vendor.logo && vendor.logo.length > 0
      ? vendor.logo[0].thumbnails?.small?.url || vendor.logo[0].url
      : null;

  const tags = buildVendorTags(vendor);

  return (
    <div className="admin-vendors-accordion-item">
      {/* Accordion Header */}
      <div className="admin-vendors-accordion-header" onClick={onToggle}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={`${vendor.vendorName} logo`}
            className="admin-vendors-accordion-logo"
          />
        ) : (
          <span className="admin-vendors-type-icon-accordion">
            <FaTools />
          </span>
        )}
        <div className="admin-vendors-accordion-title">
          <span className="admin-vendors-accordion-name">
            {vendor.vendorName}
          </span>
          <span className="admin-vendors-accordion-subtitle">
            {categoryDisplay}
          </span>
        </div>
        <span
          className={`admin-vendors-accordion-chevron ${
            isExpanded ? "expanded" : ""
          }`}
        >
          <FaChevronDown />
        </span>
      </div>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="admin-vendors-accordion-content">
          {/* View Details Button */}
          <button
            className="admin-vendors-accordion-goto"
            onClick={(e) => {
              e.stopPropagation();
              onView(vendor.id);
            }}
          >
            <span>View More Information</span>
            <FaExternalLinkAlt />
          </button>

          {/* Status Badge */}
          <div className="admin-vendors-accordion-row">
            <span
              className={`admin-vendors-status-badge ${
                vendor.status === "Active" ? "badge-active" : "badge-inactive"
              }`}
            >
              {vendor.status}
            </span>
          </div>

          {/* Details */}
          <div className="admin-vendors-accordion-details">
            {/* Contact Person */}
            {vendor.contactName && (
              <div className="admin-vendors-accordion-detail">
                <FaUserTie className="admin-vendors-accordion-icon" />
                <span>{vendor.contactName}</span>
              </div>
            )}

            {/* Phone */}
            <div className="admin-vendors-accordion-detail">
              <FaPhone className="admin-vendors-accordion-icon" />
              <span>
                {formatPhoneDisplay(vendor.contactPhone) || "No phone"}
              </span>
            </div>

            {/* Email */}
            <div className="admin-vendors-accordion-detail">
              <FaEnvelope className="admin-vendors-accordion-icon" />
              <span>{vendor.contactEmail || "No email"}</span>
            </div>

            {/* Address */}
            <div className="admin-vendors-accordion-detail">
              <FaMapMarkerAlt className="admin-vendors-accordion-icon" />
              <span>{vendor.address || "No address"}</span>
            </div>

            {/* Website */}
            <div className="admin-vendors-accordion-detail">
              <FaGlobe className="admin-vendors-accordion-icon" />
              <span>{vendor.website || "No website"}</span>
            </div>
          </div>

          {/* Tags */}
          <div className="admin-vendors-card-footer-accordion">
            <div className="admin-vendors-card-tags">
              <span className="admin-vendors-tags-label">Tags: </span>
              <div className="admin-vendors-tags-pills">
                {tags.map((tag) => (
                  <span
                    key={tag.key}
                    className={`admin-vendors-tag-pill ${tag.className}`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================
   MAIN COMPONENT
   ============================================ */

const AdminVendors = ({ userData, showTestAccounts = false }) => {
  // State Management
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("card");
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [filters, setFilters] = useState({
    vendorCategory: "all",
    hasShowroom: "all",
    isClientFacing: "all",
  });
  const [expandedAccordionId, setExpandedAccordionId] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [sortBy, setSortBy] = useState("vendorName");
  const [sortDirection, setSortDirection] = useState("asc");

  // Seed view/sort/showInactive from saved user preferences once on
  // load. The `preferencesLoaded` ref prevents later hook re-renders
  // from overwriting in-session user changes.
  const { preferences, isLoading: preferencesLoading } = useUserPreferences();
  const preferencesLoaded = useRef(false);

  useEffect(() => {
    if (preferencesLoaded.current) return;
    if (preferencesLoading) return;
    setViewMode(preferences.vendorsDefaultView);
    setSortBy(preferences.vendorsDefaultSort);
    setSortDirection(preferences.vendorsSortDirection);
    setShowInactive(preferences.vendorsShowInactive);
    preferencesLoaded.current = true;
  }, [
    preferencesLoading,
    preferences.vendorsDefaultView,
    preferences.vendorsDefaultSort,
    preferences.vendorsSortDirection,
    preferences.vendorsShowInactive,
  ]);

  // Reset sort if Status was active and showInactive is turned off
  useEffect(() => {
    if (!showInactive && sortBy === "status") {
      setSortBy("vendorName");
      setSortDirection("asc");
    }
  }, [showInactive, sortBy]);

  // Fetch vendor category options from Meta API
  const { data: vendorCategoryNames = [] } = useVendorCategories();

  // Build category dropdown options from dynamic data
  const categoryOptions = useMemo(() => {
    const options = vendorCategoryNames.map((name) => ({
      value: name,
      label: name,
    }));
    return [{ value: "all", label: "All Categories" }, ...options];
  }, [vendorCategoryNames]);

  // Fetch vendors data
  const {
    data: vendors,
    isLoading,
    error,
  } = useAdminVendors({
    // Default view: show Active only. Toggle includes inactive (fetch all).
    status: showInactive ? undefined : "Active",
    vendorCategory:
      filters.vendorCategory !== "all" ? filters.vendorCategory : undefined,
    hasShowroom:
      filters.hasShowroom !== "all" ? filters.hasShowroom : undefined,
    isClientFacing:
      filters.isClientFacing !== "all" ? filters.isClientFacing : undefined,
  });

  // Vendor Modal state
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false);
  const createVendorMutation = useCreateVendor();
  const updateVendorMutation = useUpdateVendor();

  /**
   * Calculate active filter count
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.vendorCategory !== "all") count++;
    if (filters.hasShowroom !== "all") count++;
    if (filters.isClientFacing !== "all") count++;
    return count;
  }, [filters]);

  /**
   * Filter and search vendors
   */
  const filteredVendors = useMemo(() => {
    if (!vendors) return [];

    const filtered = vendors.filter((vendor) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        vendor.vendorName?.toLowerCase().includes(searchLower) ||
        vendor.contactName?.toLowerCase().includes(searchLower) ||
        vendor.contactPhone?.toLowerCase().includes(searchLower) ||
        vendor.contactEmail?.toLowerCase().includes(searchLower);

      return matchesSearch;
    });

    const sortKeyMap = {
      vendorName: (v) => (v.vendorName || "").toLowerCase(),
      category: (v) => {
        const first = Array.isArray(v.vendorCategory)
          ? v.vendorCategory[0]
          : v.vendorCategory;
        return (first || "").toLowerCase();
      },
      isClientFacing: (v) => (toBoolean(v.isClientFacing) ? 0 : 1),
      hasShowroom: (v) => (toBoolean(v.hasShowroom) ? 0 : 1),
      status: (v) => (v.status || "").toLowerCase(),
    };
    const getKey = sortKeyMap[sortBy] || sortKeyMap.vendorName;
    const dir = sortDirection === "desc" ? -1 : 1;

    return [...filtered].sort((a, b) => {
      const ka = getKey(a);
      const kb = getKey(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      const na = (a.vendorName || "").toLowerCase();
      const nb = (b.vendorName || "").toLowerCase();
      return na.localeCompare(nb);
    });
  }, [vendors, searchTerm, sortBy, sortDirection]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      vendorCategory: "all",
      hasShowroom: "all",
      isClientFacing: "all",
    });
  };

  /**
   * Handle vendor click - open modal
   */
  const handleVendorClick = (vendorId) => {
    setSelectedVendorId(vendorId);
    setShowVendorModal(true);
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="loading-container">
        <FaSpinner className="admin-dash-spinner" />
        <span className="loading-text">Loading vendors...</span>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="admin-vendors-page">
        <div className="state-container">
          <FaExclamationTriangle className="state-icon error" />
          <h2 className="state-title">Error Loading Vendors</h2>
          <p className="state-message">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-vendors-page">
      {/* Page Banner */}
      <AdminPageBanner
        title="Vendors"
        subtitle="Manage subcontractors, suppliers, and trade partners"
        count={filteredVendors.length}
        countLabel="Vendors"
        addButton={{
          label: "New Vendor",
          onClick: () => setShowCreateVendorModal(true),
        }}
      />

      {/* Search and Controls */}
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        activeFilterCount={activeFilterCount}
        showInactive={showInactive}
        onToggleShowInactive={(checked) => setShowInactive(checked)}
        onAddClick={() => setShowCreateVendorModal(true)}
      />

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isVisible={showFilters}
        categoryOptions={categoryOptions}
      />

      {/* Vendors Display */}
      {filteredVendors.length === 0 ? (
        <div className="state-container">
          <FaTools className="state-icon" />
          <h2 className="state-title">No Vendors Found</h2>
          <p className="state-message">
            {searchTerm || activeFilterCount > 0
              ? "Try adjusting your search or filters."
              : "No vendors have been created yet."}
          </p>
        </div>
      ) : viewMode === "card" ? (
        /* Card View */
        <div className="admin-vendors-grid">
          {filteredVendors.map((vendor) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              onClick={handleVendorClick}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <>
          {/* Desktop/Tablet Table View */}
          <div className="admin-vendors-table-container hide-mobile">
            <table className="admin-vendors-table">
              <thead>
                <tr>
                  {[
                    { key: "vendorName", label: "Vendor", className: "" },
                    { key: null, label: "Phone", className: "hide-md" },
                    { key: "category", label: "Category", className: "" },
                    {
                      key: "isClientFacing",
                      label: "Client-Facing",
                      className: "admin-vendors-th-center",
                    },
                    {
                      key: "hasShowroom",
                      label: "Showroom",
                      className: "admin-vendors-th-center",
                    },
                    {
                      key: "status",
                      label: "Status",
                      className: "",
                      sortable: showInactive,
                    },
                  ].map((col) => {
                    const isSortable = col.key && col.sortable !== false;
                    if (!isSortable) {
                      return (
                        <th key={col.label} className={col.className}>
                          {col.label}
                        </th>
                      );
                    }
                    const isActive = sortBy === col.key;
                    const classes = [
                      "admin-vendors-sortable-th",
                      col.className,
                      isActive ? "active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <th
                        key={col.key}
                        className={classes}
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="admin-vendors-sortable-th-inner">
                          <span>{col.label}</span>
                          {isActive && (
                            <span className="admin-vendors-sort-chevron">
                              {sortDirection === "asc" ? (
                                <FaChevronDown />
                              ) : (
                                <FaChevronUp />
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredVendors.map((vendor) => (
                  <VendorRow
                    key={vendor.id}
                    vendor={vendor}
                    onClick={handleVendorClick}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Accordion View */}
          <div className="admin-vendors-accordion show-mobile">
            {filteredVendors.map((vendor) => (
              <MobileAccordionItem
                key={vendor.id}
                vendor={vendor}
                isExpanded={expandedAccordionId === vendor.id}
                onToggle={() =>
                  setExpandedAccordionId(
                    expandedAccordionId === vendor.id ? null : vendor.id,
                  )
                }
                onView={handleVendorClick}
              />
            ))}
          </div>
        </>
      )}

      {/* Vendor Info Modal */}
      {showVendorModal && selectedVendorId && (
        <VendorInfoModal
          vendorId={selectedVendorId}
          isOpen={showVendorModal}
          onClose={() => {
            setShowVendorModal(false);
            setSelectedVendorId(null);
          }}
          showTestAccounts={showTestAccounts}
        />
      )}

      {/* Vendor Modal — Full Add mode */}
      <VendorModal
        isOpen={showCreateVendorModal}
        onClose={() => setShowCreateVendorModal(false)}
        mode="full"
        mutations={{
          createVendor: createVendorMutation,
          updateVendor: updateVendorMutation,
        }}
      />
    </div>
  );
};

export default AdminVendors;
